import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconInfoCircle } from "@tabler/icons-react";
import { cn } from "../../../lib/utils";

// The shared dark panel every evidence popover/tooltip uses — one look for
// "here is what this means", whether it hangs off an "i" icon, a chip, or a
// citation number. `fixed` and portalled to the body (see `PopoverPanel`).
const POPOVER_PANEL_CLASS =
  "fixed z-9999 w-56 rounded-xl bg-ds-black p-2.5 text-xs/relaxed font-normal text-white shadow-lg";

// Gap between the trigger and the panel, and the least room kept between the
// panel and a viewport edge, in CSS pixels. Numbers rather than classes: the
// panel is placed by measurement, so the arithmetic has to use them.
const OFFSET_PX = 6;
const EDGE_PX = 8;

/**
 * Shared reveal state for the evidence popovers/tooltips: `open` is toggled by
 * tap (touch and desktop alike), `hovered` follows the mouse. Hover is
 * mouse-only by `pointerType`, because touch fires pointerenter on tap and
 * would wedge a "hover" nothing ever clears.
 *
 * The panel is placed by MEASUREMENT against the viewport, not declared above
 * or below its trigger. It used to render in flow, `absolute` inside the
 * trigger, and choose its side from a fixed 200px allowance: in the Evidence
 * and Sources overlays that allowance was shorter than the panel plus the
 * floating tab pill, and the overlay's stacking context put the panel UNDER
 * the pill, so a chip a little below the pill opened a panel nobody could read.
 * It now opens below when it fits there (above otherwise, or on the roomier
 * side when neither fits), stays inside the viewport, follows its
 * trigger on any scroll, and paints above every surface.
 *
 * @param align which trigger edge the panel lines up with.
 */
export function usePopoverReveal(align: "left" | "right") {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [placement, setPlacement] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const panelRef = useRef<HTMLSpanElement | null>(null);
  const visible = open || hovered;

  // Layout effect: the panel renders unplaced (hidden), is measured here, and
  // the placed re-render lands before the browser paints either.
  useLayoutEffect(() => {
    if (!visible) return;

    function place(): void {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (anchor === null || panel === null)
        throw new Error("should not happen: a visible popover is not mounted");
      const trigger = anchor.getBoundingClientRect();
      const { width, height } = panel.getBoundingClientRect();
      const roomBelow = window.innerHeight - trigger.bottom;
      const below =
        roomBelow >= height + OFFSET_PX + EDGE_PX || roomBelow >= trigger.top;
      // `clientWidth`, not `innerWidth`: a fixed box resolves against the
      // initial containing block, which excludes a classic scrollbar.
      const viewportWidth = document.documentElement.clientWidth;
      const wanted = align === "left" ? trigger.left : trigger.right - width;
      const wantedTop = below
        ? trigger.bottom + OFFSET_PX
        : trigger.top - OFFSET_PX - height;
      setPlacement({
        // Kept on screen when neither side has room for the whole panel (a
        // short viewport, a long title): it then overlaps its trigger rather
        // than hanging off an edge. A panel taller than the viewport starts at
        // the top edge.
        top: Math.max(
          Math.min(wantedTop, window.innerHeight - height - EDGE_PX),
          EDGE_PX
        ),
        left: Math.min(
          Math.max(wanted, EDGE_PX),
          viewportWidth - width - EDGE_PX
        ),
      });
    }

    place();
    // `true`: the trigger moves with ANY ancestor scroller (the conversation,
    // an overlay, a bottom sheet), none of which fires on `window` itself.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [visible, align]);

  // A tap-opened panel closes on the first scroll gesture or the first tap
  // outside it. No backdrop catches that tap: a full-screen layer also caught
  // the swipe, which then closed the panel and scrolled nothing, so the user
  // had to swipe twice. The page underneath now takes the gesture as usual,
  // and these window listeners watch it in the capture phase (so before any
  // handler of the page):
  // - `touchmove`, `wheel` and `scroll` close the panel while the page
  //   scrolls. A swipe on the panel itself scrolls nothing and closes it too;
  // - an outside `click` closes the panel and is swallowed, so the tap that
  //   dismisses does not also open the source card it landed on. A tap on
  //   the trigger is left to its own toggle, one on the panel does nothing.
  useEffect(() => {
    if (!open) return;
    function close(): void {
      setOpen(false);
      setHovered(false);
    }
    function closeOnOutsideTap(event: MouseEvent): void {
      const path = event.composedPath();
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (anchor !== null && path.includes(anchor)) return;
      if (panel !== null && path.includes(panel)) return;
      event.preventDefault();
      event.stopPropagation();
      close();
    }
    const passive = { capture: true, passive: true };
    window.addEventListener("touchmove", close, passive);
    window.addEventListener("wheel", close, passive);
    window.addEventListener("scroll", close, passive);
    window.addEventListener("click", closeOnOutsideTap, true);
    return () => {
      window.removeEventListener("touchmove", close, passive);
      window.removeEventListener("wheel", close, passive);
      window.removeEventListener("scroll", close, passive);
      window.removeEventListener("click", closeOnOutsideTap, true);
    };
  }, [open]);

  return {
    open,
    visible,
    placement,
    // Goes on the wrapper span (trigger + panel). The panel is portalled, but
    // React routes pointer enter/leave through the component tree, so moving
    // the mouse onto the panel itself still keeps it open.
    anchorRef,
    panelRef,
    anchorHandlers: {
      onPointerEnter(event: React.PointerEvent): void {
        if (event.pointerType !== "mouse") return;
        setHovered(true);
      },
      onPointerLeave(event: React.PointerEvent): void {
        if (event.pointerType !== "mouse") return;
        setHovered(false);
      },
    },
    toggle(): void {
      setOpen((value) => !value);
    },
    // Clears BOTH flags. A tap-opened panel is usually dismissed with the
    // pointer still over its trigger, so leaving `hovered` set would keep
    // `visible` true once the tap is closed — a panel stuck on screen with no
    // way to close it. Safari makes this worse by skipping `pointerleave` when
    // the node under the cursor unmounts.
    dismiss(): void {
      setOpen(false);
      setHovered(false);
    },
  };
}

/** What `usePopoverReveal` hands to `PopoverPanel`, inferred rather than restated. */
type PopoverReveal = ReturnType<typeof usePopoverReveal>;

/**
 * The revealed panel, portalled to the body so that no scroller clips it and
 * no stacking context (the conversation overlays, the floating tab pill) sits
 * over it. Hidden for the one render before its first measurement.
 */
export function PopoverPanel({
  reveal,
  className,
  children,
}: {
  reveal: PopoverReveal;
  className: string | null;
  children: React.ReactNode;
}): React.ReactPortal {
  return createPortal(
    <span
      ref={reveal.panelRef}
      role="tooltip"
      className={cn(POPOVER_PANEL_CLASS, className)}
      style={
        reveal.placement === null
          ? { top: 0, left: 0, visibility: "hidden" }
          : reveal.placement
      }
    >
      {children}
    </span>,
    document.body
  );
}

/**
 * A tap-or-hover info tooltip: an "i" icon that toggles a small popover on tap
 * (touch and desktop alike), and reveals it on mouse hover. Built here because
 * the webapp has no tooltip primitive and the citation sheet is a bottom-sheet
 * modal (hover alone can't serve touch). A tapped-open panel closes on an
 * outside tap or a scroll (see `usePopoverReveal`), and clicks are stopped
 * from bubbling so opening one inside a tappable row/badge doesn't also
 * trigger that row.
 */
export function InfoPopover({
  label,
  align,
  children,
}: {
  label: string;
  align: "left" | "right";
  children: React.ReactNode;
}) {
  const reveal = usePopoverReveal(align);
  return (
    <span className="relative inline-flex align-middle">
      {/* Hover is tracked on this span, and the panel is measured against it. */}
      <span
        ref={reveal.anchorRef}
        {...reveal.anchorHandlers}
        className="inline-flex"
      >
        <button
          type="button"
          aria-label={label}
          onClick={(event) => {
            event.stopPropagation();
            reveal.toggle();
          }}
          className="inline-flex items-center justify-center text-ds-slate-400 hover:text-ds-slate-600"
        >
          <IconInfoCircle size={15} />
        </button>
        {reveal.visible && (
          <PopoverPanel reveal={reveal} className={null}>
            {children}
          </PopoverPanel>
        )}
      </span>
    </span>
  );
}

/**
 * A chip that explains itself: the label is the trigger (not a separate "i"
 * icon — the Evidence tab would drown in icons), tapped to toggle the
 * explanation and hovered to reveal it with a mouse. Used for the Evidence
 * tab's status / relation / confidence chips, whose meaning and derivation a
 * clinician must be able to interrogate in place.
 */
export function ExplainedChip({
  className,
  label,
  title,
  children,
}: {
  // The chip's own look (palette + shape) — the trigger renders exactly as the
  // plain chip did, plus a pointer cursor.
  className: string;
  label: React.ReactNode;
  // The popover's bold first line (usually the label itself, or the score's
  // full name).
  title: string;
  children: React.ReactNode;
}) {
  const reveal = usePopoverReveal("left");
  return (
    <span className="relative inline-flex">
      {/* Hover-tracked span, as in InfoPopover. */}
      <span
        ref={reveal.anchorRef}
        {...reveal.anchorHandlers}
        className="inline-flex"
      >
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            reveal.toggle();
          }}
          className={cn("cursor-pointer", className)}
        >
          {label}
        </button>
        {reveal.visible && (
          <PopoverPanel reveal={reveal} className={null}>
            <span className="block font-semibold text-white">{title}</span>
            {children}
          </PopoverPanel>
        )}
      </span>
    </span>
  );
}
